import {
  FacebookPage,
  InstagramAccount,
  InstagramPost,
  InstagramPostsPaging,
} from "@markit/common.types";
import { MARKITAI } from "@markit/common.utils";
import { API } from "../API";

export const FACEBOOK_APP_ID = "720442611131425";

// Facebook's OAuth redirect_uri must use a registered domain (markitai.com).
const FACEBOOK_OAUTH_REDIRECT_URI = `${MARKITAI}/facebookApi/redirect`;

// Builds the Facebook Login OAuth dialog URL. Unlike Instagram's OAuth (resolves directly
// to one account), this can resolve to multiple Pages the creator manages - handled by
// the post-redirect picker step, see FacebookConnectionPage.tsx.
export const getFacebookOAuthURL = (userId: string) => {
  const params = new URLSearchParams({
    client_id: FACEBOOK_APP_ID,
    redirect_uri: FACEBOOK_OAUTH_REDIRECT_URI,
    response_type: "code",
    scope: [
      "business_management",
      "pages_show_list",
      "pages_messaging",
      "pages_manage_metadata",
      "pages_read_engagement",
      "pages_manage_engagement",
      "pages_read_user_content",
    ].join(","),
    state: userId,
  });

  return `https://www.facebook.com/v21.0/dialog/oauth?${params.toString()}`;
};

// Fetches the Pages the creator manages from the pending OAuth session, for the
// post-redirect picker step (FacebookConnectionPage.tsx).
export const listFacebookPages = async () => {
  const response = await API.facebookApi.listFacebookPages({});
  return response.pages;
};

// Finalizes connecting the chosen Page.
export const connectFacebookPage = (pageId: string) =>
  API.facebookApi.connectFacebookPage({ pageId });

export const disconnectFacebookPage = (pageId: string) =>
  API.facebookApi.handleFacebookPageDisconnect({ pageId });

/**
 * Authenticated GET to `/facebookApi/getPagePosts` (Firebase Bearer token). Same
 * InstagramPost/InstagramPostsPaging response shape as Instagram's post fetch, since
 * Facebook Page posts are normalized into that shape server-side. Paginate with
 * `after` from the previous response's `paging.nextCursor`.
 * Throws on failure (including a stale/invalidated connection) so callers can tell
 * a fetch error apart from a genuinely empty Page - do not swallow here.
 */
export const fetchFacebookPostsPage = async (
  pageId: string,
  windowSize: number,
  after?: string,
  contentId?: string
): Promise<{ posts: InstagramPost[]; paging: InstagramPostsPaging }> => {
  const response = await API.facebookApi.getPagePosts({
    pageId,
    windowSize,
    afterCursor: after ?? null,
    specificPostId: contentId ?? null,
  });
  if (!response) {
    throw new Error("Failed to fetch Facebook posts");
  }
  return response;
};

// Re-syncs Page name/profile pic and webhook subscription for a connected Page
export const refreshFacebookPage = async (pageId: string) => {
  const response = await API.facebookApi.handleFacebookRefreshAccount({
    pageId,
  });
  return response;
};

// SocialProfilePic is typed specifically to InstagramAccount (username, not pageName) -
// this adapts a FacebookPage into that same shape so the component can be reused as-is
// rather than forking it. Field-by-field, not a spread, so an extra `pageName` field
// never leaks into a type that doesn't declare it.
export const facebookPageAsProfilePicAccount = (
  facebookPage: FacebookPage
): InstagramAccount => ({
  id: facebookPage.id,
  createdBy: facebookPage.createdBy,
  tokenHash: facebookPage.tokenHash,
  tokenExpiration: facebookPage.tokenExpiration,
  connectedOn: facebookPage.connectedOn,
  status: facebookPage.status,
  username: facebookPage.pageName,
  profilePicUrl: facebookPage.profilePicUrl,
});
